import './styles.css';
import { useState, useEffect, useCallback } from 'react';
import { Grid, Snackbar} from '@material-ui/core';
import { Alert} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import AnswerForm from '../AnswerForm';
import RevealForm from '../RevealForm';
import EndSessionModal from '../EndSessionModal';
import OptionalTimer from '../OptionalTimer';
import DonationPrompt from '../DonationPrompt';
import SingleNotesPaginatedCard from '../SingleNotesPaginatedCard';
import { shuffleDataArray } from '../../hooks/dataHooks';
import { midiPlaySound } from '../../hooks/midiPlaySound';
import { MIDI_CORRECT_SOUND_CHOICE, MIDI_INCORRECT_SOUND_CHOICE, correctAnswersResponse } from '../../utils';
import useTimer from '../../hooks/timerHooks';
import InvisibleMidiSound from '../InvisibleMidiSound';
import { useAnime } from '../../hooks/AnimeProvider';

const MIDI_CORRECT_LENGTH = 3;
const MIDI_INCORRECT_LENGTH = 4;

const PracticeSession = (props) => {
  const {isSmallScreen, isTinyMobileScreen, selectedData, has3rdPos, isMix1stAnd3rdPos, path, keyName} = props;
  const [dataArray, setDataArray] = useState([]);
  const [dataIndex, setDataIndex] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState([]);
  const [wrongAnswers, setWrongAnswers] = useState([]);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [isQuestionCorrect, setIsQuestionCorrect] = useState(false);
  const [isModalOpen, setIsModalOpen]= useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSecondAttemptQuestions, setIsSecondAttemptQuestions] = useState(false);
  const [isReveal, setIsReveal] = useState(false);
  const [incorrectAnswerMessage, setIncorrectAnswerMessage] = useState('');
  const [correctAnswerMessage, setCorrectAnswerMessage] = useState('');
  const {timer, isActive, handleStart, handlePause, isPaused, setTimer, setIsActive, setIsPaused} = useTimer(0);
  const isSideBySide = useMediaQuery('(min-width:960px)');
  const {handleTriggerAnime} = useAnime();

  useEffect(() => {
    setDataArray([]);
    setCorrectAnswers([]);
    setWrongAnswers([]);
    const shuffledArray = shuffleDataArray(selectedData); 
    setDataIndex(0);
    setIsSecondAttemptQuestions(false);
    setIsReveal(false);
    isMix1stAnd3rdPos ? setDataArray(shuffledArray.slice(0, 10)) : setDataArray(shuffledArray);

  }, [selectedData, path, isMix1stAnd3rdPos])

  const getIncorrectAnswersMessage = (noteNameCorrect, fingeringCorrect, handPositionCorrect, positionCorrect) => {
    let incorrectItems = []
    if(!noteNameCorrect) {
      incorrectItems.push(' NAME')
    }
    if(!fingeringCorrect) {
      incorrectItems.push(' FINGERING')
    }
    if(!handPositionCorrect) {
      incorrectItems.push(' HAND POSITION')
    }
    if(!positionCorrect) {
      incorrectItems.push(' FINGER PLACEMENT')
    }

    let message = '';

    if(incorrectItems.length === 1) {
      message = `Sorry, the${incorrectItems[0]} was incorrect!`
    } else {
     message = 'Sorry, the following were incorrect:'
     for (let i = 0; i < incorrectItems.length; i++) {
       if(i === incorrectItems.length - 1) {
         message += ' and' + incorrectItems[i]
       } else {
         message += incorrectItems[i] + ",";
       }
     }
     message += '!'
    }
    setIncorrectAnswerMessage(message)
  }

  const onSubmit = (noteBaseName, accidental, chosenString, fingering, handPosition, position) => {
    // Step 1 first compare if the data is correct
    const question = dataArray[dataIndex];

    const noteNameCorrect = question.noteBaseName === noteBaseName && question.accidental === accidental;
    const fingeringCorrect = isMix1stAnd3rdPos ? true : (question.chosenString.some(string => string === chosenString) && question.fingering.some(f => f === fingering));
    const handPositionCorrect = isMix1stAnd3rdPos ? question.handPosition === handPosition : true;
    const positionCorrect = question.position.some(p => p === position)

  
    const isEverythingCorrect = noteNameCorrect && fingeringCorrect && handPositionCorrect && positionCorrect;

    setIsQuestionCorrect(isEverythingCorrect)
    // Step 2 then push the correct and incorrect data in the designated array 
    if(isEverythingCorrect){
      setCorrectAnswers([...correctAnswers, question]);
      handleTriggerAnime();
      const randomisedCheer = correctAnswersResponse[Math.floor(Math.random() * correctAnswersResponse.length)]
      setCorrectAnswerMessage(randomisedCheer)
      midiPlaySound(4, 4, MIDI_CORRECT_SOUND_CHOICE, MIDI_CORRECT_LENGTH)
    } else {
      setWrongAnswers([...wrongAnswers, question])
      getIncorrectAnswersMessage(noteNameCorrect, fingeringCorrect, handPositionCorrect, positionCorrect)
      midiPlaySound(4, 4, MIDI_INCORRECT_SOUND_CHOICE, MIDI_INCORRECT_LENGTH)
    }
    // Step 3 Snackbar that tells the user whether it's correct
    setShowSnackbar(true)

    // Step 4 go to next question
    if(dataIndex === dataArray.length -1) {
      setIsModalOpen(true)
      handlePause();
    } else {
		  setIsLoading(true);
		  setTimeout(() => {
        setDataIndex(dataIndex + 1)
			  setIsLoading(false);
		  }, 1500);
	
    }
  }

  const handleUseWrongAnswersData = () => {
    setIsSecondAttemptQuestions(true);
    setDataArray(wrongAnswers);
    setDataIndex(0)
    setIsModalOpen(false);
    setWrongAnswers([]);
    setCorrectAnswers([]);
    setTimer(0);
    setIsActive(false);
    setIsPaused(false);
  }

  const handleRevealAnswers = () => {
    setIsReveal(true);
    handleUseWrongAnswersData();
  }

  const revealNextAnswer = () => setDataIndex(dataIndex + 1);
  const revealPreviousAnswer = () => setDataIndex(dataIndex -1);

  const resetEntireExcercise = useCallback(() => {
    const shuffledArray = shuffleDataArray(selectedData); 
    setIsSecondAttemptQuestions(false);
    setDataArray(shuffledArray)
    setDataIndex(0);
    setIsModalOpen(false);
    setWrongAnswers([]);
    setCorrectAnswers([]);
    setIsReveal(false);
    setTimer(0);
    setIsActive(false);
    setIsPaused(false);
  },[selectedData, setTimer, setIsActive, setIsPaused])

  return (
    <>
      <InvisibleMidiSound soundChoices={[MIDI_CORRECT_SOUND_CHOICE, MIDI_INCORRECT_SOUND_CHOICE]} />
      <Grid container direction='row' spacing={3} justifyContent="space-around">
        <EndSessionModal 
          isModalOpen={isModalOpen}
          correctAnswers={correctAnswers}
          wrongAnswers={wrongAnswers}
          totalQuestions={dataArray.length}
          handleUseWrongAnswersData={handleUseWrongAnswersData}
          resetEntireExcercise={resetEntireExcercise}
          isSecondAttemptQuestions={isSecondAttemptQuestions}
          handleRevealAnswers={handleRevealAnswers}
          keyName={keyName}
          timer={timer}
        />
        <Snackbar
          anchorOrigin={{vertical: isSmallScreen ? 'bottom' : 'top', horizontal: 'right'}}
          open={showSnackbar}
          autoHideDuration={isQuestionCorrect ? 3000 : 5000}
          onClose={()=> setShowSnackbar(false)}          
          >
          <Alert
            icon={false}
            severity={isQuestionCorrect ? 'success' : 'error'}
            variant="filled"
            sx={{ width: '100%',   
              display: 'block',
              textAlign: isQuestionCorrect ? "center" : "left"}}
            style={{borderRadius: '10px'}}
          >
            <img 
              src={isQuestionCorrect ? 
                "https://i.giphy.com/media/v1.Y2lkPTc5MGI3NjExeDV5aTl3ZDBpMzk5bHJ3NXJpZGVqejFkZDVwZTk1anowbWkxczdyNSZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9cw/5mUGrtcKCjKXE8Vsef/giphy.gif" 
                : "https://i.giphy.com/media/v1.Y2lkPTc5MGI3NjExaWIwNXNsaGRmdDVxeWNqbGdwbXV4M3dkb29vZ3FzcGRvM2RwM3BsYyZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9cw/r6FCAJvaBNYz6NtaJv/giphy.gif"} 
              style={{maxHeight: '30px', marginRight: '10px'}}
              alt={isQuestionCorrect ? "Correct Celebration Gif" : "Incorrect Warning Gif"}
            />
            {!isQuestionCorrect ? incorrectAnswerMessage : correctAnswerMessage}
            <img 
              src={isQuestionCorrect ? 
                "https://i.giphy.com/media/v1.Y2lkPTc5MGI3NjExeDV5aTl3ZDBpMzk5bHJ3NXJpZGVqejFkZDVwZTk1anowbWkxczdyNSZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9cw/5mUGrtcKCjKXE8Vsef/giphy.gif" 
                : "https://i.giphy.com/media/v1.Y2lkPTc5MGI3NjExaWIwNXNsaGRmdDVxeWNqbGdwbXV4M3dkb29vZ3FzcGRvM2RwM3BsYyZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9cw/r6FCAJvaBNYz6NtaJv/giphy.gif"} 
              style={{maxHeight: '30px', marginLeft: '10px'}}
              alt={isQuestionCorrect ? "Correct Celebration Gif" : "Incorrect Warning Gif"}
            />   
          </Alert>
        </Snackbar>
        {!!dataArray.length &&
          <Grid  item xs={12} sm={8} md={5}>
            <OptionalTimer 
              isReveal={isReveal}
              isActive={isActive}
              isPaused={isPaused}
              handlePause={handlePause}
              handleStart={handleStart}
              timer={timer}
            />
            <SingleNotesPaginatedCard 
              isTinyMobileScreen={isTinyMobileScreen}
              isSmallScreen={isSmallScreen}
              imgUrl={dataArray[dataIndex].noteImg}
              imgTitle={`note ${dataArray[dataIndex].noteBaseName}${dataArray[dataIndex].accidental}`}
              paginationLengthTotal={dataArray.length}
              paginationIndex={dataIndex + 1}
              isLoading={isLoading}
            />
            {isSideBySide && <DonationPrompt />}
          </Grid>
        }
        <Grid item xs={12} sm={8} md={5} lg={6}>
          {isReveal ?
            <RevealForm 
              dataArray={dataArray} 
              dataIndex={dataIndex} 
              revealNextAnswer={revealNextAnswer}
              revealPreviousAnswer={revealPreviousAnswer}
              resetEntireExcercise={resetEntireExcercise}
              isSmallScreen={isSmallScreen}
              has3rdPos={has3rdPos}
              isMix1stAnd3rdPos={isMix1stAnd3rdPos}
            /> 
            :
            <AnswerForm 
              fingerForMixPositionQuestion={dataArray[dataIndex]?.fingering[0]}
              onSubmit={onSubmit} 
              isSmallScreen={isSmallScreen} 
              has3rdPos={has3rdPos} 
              path={path} 
              isMix1stAnd3rdPos={isMix1stAnd3rdPos}
            />
          }
          {!isSideBySide && <DonationPrompt />}
        </Grid>
      </Grid>
    </>
  )

}

export default PracticeSession; 